import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/app/StatusBadge";
import CrudDialog, { ConfirmDeleteDialog } from "@/components/app/CrudDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PlusCircle, Pencil, Trash2, Building2, ArrowRight,
  ChevronRight, Search, Eye } from "lucide-react";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";

const MASTER_FIELDS = [
  { name: "name", label: "Company name", required: true },
  { name: "code", label: "Code (short)", required: true, help: "e.g. ACME — used in codes like ACME-EMP-001" },
  { name: "country", label: "Country ISO", placeholder: "US, GB, IN…" },
  { name: "currency", label: "Currency", placeholder: "USD" },
  { name: "abm_id", label: "ABM ID", placeholder: "APPLE-ABM-XXXXX",
    help: "Apple Business Manager organisation identifier — enables ADE / DEP / MDM enrolment for this entity." },
];

const CO_FIELDS = [
  { name: "name", label: "Subsidiary name", required: true },
  { name: "code", label: "Code", required: true },
  { name: "country", label: "Country" },
  { name: "currency", label: "Currency" },
  { name: "abm_id", label: "ABM ID", placeholder: "APPLE-ABM-XXXXX",
    help: "Apple Business Manager organisation identifier — leave blank if this subsidiary shares the master's ABM." },
];

/* ------------------------------------------------------------------ */
/* Level 1 — Master companies directory                                */
/* ------------------------------------------------------------------ */
export default function Companies() {
  const navigate = useNavigate();
  const [masters, setMasters] = useState([]);
  const [countsByMaster, setCountsByMaster] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [masterDialog, setMasterDialog] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/master-companies"),
      api.get("/companies"),
    ]).then(([a, b]) => {
      const ms = a.data.items || [];
      setMasters(ms);
      const counts = {};
      (b.data.items || []).forEach((c) => {
        const key = c.master_company_id;
        if (!counts[key]) counts[key] = { total: 0, countries: new Set() };
        counts[key].total += 1;
        if (c.country) counts[key].countries.add(c.country);
      });
      setCountsByMaster(counts);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const deleteMaster = async (m) => {
    try { await api.delete(`/master-companies/${m.id}`); toast.success(`${m.name} deactivated`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!q) return masters;
    const n = q.toLowerCase();
    return masters.filter((m) => (m.name || "").toLowerCase().includes(n)
      || (m.code || "").toLowerCase().includes(n)
      || (m.country || "").toLowerCase().includes(n));
  }, [masters, q]);

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="master-companies-directory">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Customers · Level 1</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Companies</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Master companies and their organisational structure.  Click any row to open the master and see subsidiaries.
          </p>
        </div>
        <Button variant="create" className="bg-slate-900" onClick={() => setMasterDialog({ open: true, initial: null })}
          data-testid="master-add-btn">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Add Master Company
        </Button>
      </div>
      <QuickActionGroup module="tenants" />

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" strokeWidth={1.6} />
        <Input className="pl-8" placeholder="Search name, code, country…"
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="master-search" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="master-companies-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Master Company</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Headquarters</th>
              <th className="text-left px-3 py-2">Currency</th>
              <th className="text-right px-3 py-2">Subsidiaries</th>
              <th className="text-right px-3 py-2">Countries</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-slate-500 text-center py-8">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="text-slate-500 text-center py-8">No master companies match.</td></tr>
            )}
            {filtered.map((m) => {
              const counts = countsByMaster[m.id] || {};
              return (
                <tr key={m.id}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/companies/${m.id}`)}
                  data-testid={`master-row-${m.code || m.id}`}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded bg-slate-900 text-white grid place-items-center font-heading text-[11px]">
                        {(m.code || m.name || "M").slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900 flex items-center gap-1.5">
                          {m.name}
                          {m.is_demo && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold tracking-wider">DEMO</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-mono text-[11px]">{m.code || "—"}</td>
                  <td className="px-3 py-3">{m.country || "—"}</td>
                  <td className="px-3 py-3">{m.currency || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono">{counts.total ?? 0}</td>
                  <td className="px-3 py-3 text-right font-mono">{counts.countries ? counts.countries.size : 0}</td>
                  <td className="px-3 py-3">
                    <StatusBadge value={m.active ? "active" : "inactive"} tone={m.active ? "green" : "slate"} />
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 mr-1"
                      onClick={() => setMasterDialog({ open: true, initial: m })}
                      title="Edit"
                      data-testid={`master-edit-${m.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600"
                      onClick={() => setConfirm({ target: m })}
                      title="Deactivate"
                      data-testid={`master-delete-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                  <td className="px-3 py-3 text-slate-400"><ArrowRight className="h-4 w-4" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CrudDialog
        open={masterDialog.open}
        onOpenChange={(o) => setMasterDialog({ ...masterDialog, open: o })}
        title={masterDialog.initial ? "Edit master company" : "Add master company"}
        fields={MASTER_FIELDS}
        initial={masterDialog.initial}
        endpoint={masterDialog.initial ? `/master-companies/${masterDialog.initial.id}` : "/master-companies"}
        method={masterDialog.initial ? "PATCH" : "POST"}
        onSaved={load}
        testIdPrefix="master"
      />
      <ConfirmDeleteDialog
        open={!!confirm}
        onOpenChange={() => setConfirm(null)}
        title={`Deactivate ${confirm?.target?.name}?`}
        description="Deactivating hides this master company from operational lists but preserves its history and all subsidiaries."
        onConfirm={() => deleteMaster(confirm.target)}
        testId="master-confirm"
      />
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Level 2 — inside a Master Company: subsidiaries table               */
/* ------------------------------------------------------------------ */
export function CompanySubsidiaries() {
  const { masterId } = useParams();
  const navigate = useNavigate();
  const [master, setMaster] = useState(null);
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [coDialog, setCoDialog] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/master-companies/${masterId}`),
      api.get("/companies", { params: { master_company_id: masterId } }),
    ]).then(([m, c]) => {
      setMaster(m.data);
      setSubs(c.data.items || []);
    }).catch(() => toast.error("Could not load master company"))
      .finally(() => setLoading(false));
  }, [masterId]);
  useEffect(() => { load(); }, [load, masterId]);

  const deleteCo = async (c) => {
    try { await api.delete(`/companies/${c.id}`); toast.success(`${c.name} deactivated`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!q) return subs;
    const n = q.toLowerCase();
    return subs.filter((c) => (c.name || "").toLowerCase().includes(n)
      || (c.code || "").toLowerCase().includes(n)
      || (c.country || "").toLowerCase().includes(n));
  }, [subs, q]);

  if (loading || !master) return <div className="text-slate-400 p-6">Loading…</div>;

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="company-subsidiaries">
      <nav className="text-[12px] text-slate-500 flex items-center gap-1">
        <Link to="/companies" className="hover:text-slate-800">Companies</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-900 font-medium">{master.name}</span>
        <ChevronRight className="h-3 w-3" />
        <span>Subsidiaries</span>
      </nav>

      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Master Company</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">{master.name}</h1>
          <p className="text-sm text-slate-500 mt-0.5 font-mono">
            {master.code} · {master.country || "—"} · {master.currency || "—"}
          </p>
        </div>
        <Button variant="create" className="bg-slate-900"
          onClick={() => setCoDialog({ open: true, initial: null })}
          data-testid="sub-add-btn">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Add Subsidiary
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" strokeWidth={1.6} />
        <Input className="pl-8" placeholder="Search subsidiary name, code, country…"
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="sub-search" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="subsidiaries-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Subsidiary</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-left px-3 py-2">Currency</th>
              <th className="text-left px-3 py-2">ABM ID</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-slate-500 text-center py-8">
                No subsidiaries {q ? "match your search." : "yet — add the first one to start."}
              </td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/customer/${c.id}/tech-env`)}
                data-testid={`sub-row-${c.code || c.id}`}>
                <td className="px-3 py-2 font-medium flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5 text-slate-400" />
                  {c.name}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">{c.code}</td>
                <td className="px-3 py-2">{c.country || "—"}</td>
                <td className="px-3 py-2">{c.currency || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]"
                  data-testid={`sub-abm-${c.id}`}>
                  {c.abm_id ? (
                    <span className="text-slate-800">{c.abm_id}</span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge value={c.active ? "active" : "inactive"} tone={c.active ? "green" : "slate"} />
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 mr-1"
                    onClick={() => setCoDialog({ open: true, initial: c })}
                    title="Edit"
                    data-testid={`sub-edit-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600"
                    onClick={() => setConfirm({ target: c })}
                    title="Deactivate"
                    data-testid={`sub-delete-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
                <td className="px-3 py-2 text-slate-400"><ArrowRight className="h-4 w-4" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-slate-500">
        {filtered.length} of {subs.length} subsidiaries shown.
      </div>

      <CrudDialog
        open={coDialog.open}
        onOpenChange={(o) => setCoDialog({ ...coDialog, open: o })}
        title={coDialog.initial ? "Edit subsidiary" : "Add subsidiary"}
        fields={CO_FIELDS}
        initial={coDialog.initial || { master_company_id: masterId }}
        endpoint={coDialog.initial ? `/companies/${coDialog.initial.id}` : "/companies"}
        method={coDialog.initial ? "PATCH" : "POST"}
        onSaved={load}
        testIdPrefix="sub"
      />
      <ConfirmDeleteDialog
        open={!!confirm}
        onOpenChange={() => setConfirm(null)}
        title={`Deactivate ${confirm?.target?.name}?`}
        description="Deactivating hides the subsidiary from operational lists but preserves its history."
        onConfirm={() => deleteCo(confirm.target)}
        testId="sub-confirm"
      />
    </div>
  );
}
